import {IconCircleCheck, IconLock, IconPlus, IconTrash} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import useGetUserInfo from "../../../hooks/useGetUserInfo";
import useRegisterChileanRUT from "../../../hooks/useRegisterChileanRUT";
import {
  useCreateChileanBankAccount,
  useDeleteChileanBankAccount,
  useGetChileanBankAccounts,
} from "../../../hooks/wallet/useChileanBankAccounts";
import useGetUserWallet from "../../../hooks/wallet/useGetUserWallet";
import {useGetPaykuBanks, usePostPaykuWithdrawal} from "../../../hooks/wallet/usePostPaykuWithdrawal";
import useCurrencyStore from "../../../store/useCurrency.store";
import {formatCurrencyAmount} from "../../../util/balance.util";
import styles from "./PaykuWithdrawal.module.css";

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const MIN_WITHDRAWAL_CLP = 5000;

const formatClpPesos = amount => {
  const value = Number(amount) || 0;
  return `${value.toLocaleString("es-CL", {maximumFractionDigits: 0})} CLP`;
};

const CHILE_ACCOUNT_TYPES = {
  1: "Cuenta Corriente",
  2: "Cuenta Vista/RUT",
  3: "Cuenta de Ahorro",
};

// Módulo 11 (SII) check-digit validation.
const validateChileanRUT = rut => {
  if (!rut) return false;
  const cleanRUT = rut.replace(/[^0-9kK]/g, "").toUpperCase();
  if (!/^\d{7,8}[0-9K]$/.test(cleanRUT)) return false;

  const body = cleanRUT.slice(0, -1);
  const providedDv = cleanRUT.slice(-1);
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number.parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const remainder = 11 - (sum % 11);
  let expectedDv;
  if (remainder === 11) expectedDv = "0";
  else if (remainder === 10) expectedDv = "K";
  else expectedDv = String(remainder);
  return providedDv === expectedDv;
};

const normalizeRUT = rut => (rut || "").replace(/[^0-9kK]/g, "").toUpperCase();

const formatRUTForDisplay = rut => {
  const clean = normalizeRUT(rut);
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const grouped = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${grouped}-${dv}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PaykuWithdrawal = ({onBack, onCancel}) => {
  const {t} = useTranslation();
  const {selectedCurrency} = useCurrencyStore();
  const {getAccountByCurrency} = useGetUserWallet();
  const {data: userData, isLoading: userLoading, refetch: refetchUserInfo} = useGetUserInfo();

  // RUT from profile
  const storedRUT = useMemo(() => normalizeRUT(userData?.nationalID_CL), [userData?.nationalID_CL]);
  const hasStoredRUT = storedRUT.length > 0;
  const profileFullName = useMemo(() => {
    const first = userData?.firstName || userData?.given_name || "";
    const last = userData?.lastName || userData?.family_name || "";
    return `${first} ${last}`.trim();
  }, [userData?.firstName, userData?.lastName, userData?.given_name, userData?.family_name]);

  // Bank accounts
  const {accounts, isLoading: accountsLoading, refetch: refetchAccounts} = useGetChileanBankAccounts();
  const {mutate: createAccount, isPending: creatingAccount} = useCreateChileanBankAccount();
  const {mutate: deleteAccount} = useDeleteChileanBankAccount();
  const hasAccounts = accounts.length > 0;

  // RUT registration
  const [rutInput, setRutInput] = useState("");
  const [rutFeedback, setRutFeedback] = useState(null);
  const {mutate: registerRUT, isPending: rutPending} = useRegisterChileanRUT();

  // Bank account creation form
  const [newAccount, setNewAccount] = useState({
    bank_name: "",
    bank_sbif_code: "",
    account_type: 2,
    account_number: "",
    beneficiary_name: "",
    email: "",
    phone: "",
  });
  const [accountFeedback, setAccountFeedback] = useState(null);

  // Step management: "selectAccount" | "createAccount" | "withdraw"
  const [step, setStep] = useState("selectAccount");
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Withdrawal form
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  const {mutate: createWithdrawal, isPending: withdrawPending} = usePostPaykuWithdrawal();
  const {data: banksData, isLoading: banksLoading} = useGetPaykuBanks("CLP");

  const isValidCurrency = selectedCurrency?.id === "CLP";
  const currentCurrencyAccount = selectedCurrency ? getAccountByCurrency(selectedCurrency.id) : null;
  const availableBalance = currentCurrencyAccount?.balance || 0;
  const formattedBalance = formatCurrencyAmount(availableBalance, "CLP", "$");

  // Prefill beneficiary name from profile when creating account
  useEffect(() => {
    if (profileFullName && !newAccount.beneficiary_name) {
      setNewAccount(prev => ({...prev, beneficiary_name: profileFullName}));
    }
  }, [profileFullName, newAccount.beneficiary_name]);

  // Prefill email from profile
  useEffect(() => {
    if (userData?.email && !newAccount.email) {
      setNewAccount(prev => ({...prev, email: userData.email}));
    }
  }, [userData?.email, newAccount.email]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRegisterRUT = e => {
    e.preventDefault();
    if (!validateChileanRUT(rutInput)) {
      setRutFeedback({type: "error", text: t("withdrawalPaykuValidRUT")});
      return;
    }
    setRutFeedback(null);
    registerRUT(normalizeRUT(rutInput), {
      onSuccess: () => refetchUserInfo(),
      onError: err => {
        const apiMessage = err?.response?.data?.message;
        setRutFeedback({type: "error", text: apiMessage || t("withdrawalPaykuRUTRegistrationError")});
      },
    });
  };

  const resolveBankCode = bank =>
    bank?.sbif || bank?.code || bank?.id || bank?.bank_code || bank?.bank_id || bank?.value || bank?.name || "";

  const handleBankSelect = e => {
    const banks = banksData ?? [];
    const idx = Number.parseInt(e.target.value, 10);
    const selectedBank = Number.isInteger(idx) ? banks[idx] : null;
    setNewAccount(prev => ({
      ...prev,
      bank_sbif_code: resolveBankCode(selectedBank),
      bank_name: selectedBank?.name ?? "",
    }));
  };

  const handleCreateAccount = e => {
    e.preventDefault();
    setAccountFeedback(null);

    if (!newAccount.bank_sbif_code || !newAccount.account_number || !newAccount.beneficiary_name) {
      setAccountFeedback({type: "error", text: t("withdrawalPaykuBankAccountRequiredFields")});
      return;
    }

    const payload = {
      rut: storedRUT,
      bank_name: newAccount.bank_name,
      bank_sbif_code: newAccount.bank_sbif_code,
      account_type: newAccount.account_type,
      account_number: newAccount.account_number,
      beneficiary_name: newAccount.beneficiary_name,
      email: newAccount.email,
      phone: newAccount.phone,
    };

    createAccount(payload, {
      onSuccess: () => {
        refetchAccounts();
        setStep("selectAccount");
        // Reset form for next time
        setNewAccount({
          bank_name: "",
          bank_sbif_code: "",
          account_type: 2,
          account_number: "",
          beneficiary_name: profileFullName || "",
          email: userData?.email || "",
          phone: "",
        });
      },
      onError: err => {
        const apiMessage = err?.response?.data?.message;
        setAccountFeedback({type: "error", text: apiMessage || t("withdrawalPaykuBankAccountCreateError")});
      },
    });
  };

  const handleDeleteAccount = id => {
    deleteAccount(id, {onSuccess: () => refetchAccounts()});
  };

  const handleSubmitWithdrawal = e => {
    e.preventDefault();
    setFeedback(null);

    const amount = Number(withdrawalAmount);
    if (!amount || amount < MIN_WITHDRAWAL_CLP) {
      setFeedback({type: "error", text: t("withdrawalPaykuMinAmount")});
      return;
    }
    if (amount > availableBalance) {
      setFeedback({type: "error", text: t("withdrawalPaykuInsufficientBalance")});
      return;
    }
    if (!selectedAccount) {
      setFeedback({type: "error", text: t("withdrawalPaykuSelectAccountRequired")});
      return;
    }

    const withdrawalData = {
      amount,
      currency: "CLP",
      beneficiaryName: selectedAccount.beneficiary_name,
      email: selectedAccount.email,
      phone: selectedAccount.phone,
      accountbank_name: selectedAccount.bank_name,
      accountbank_rut: selectedAccount.rut,
      accountbank_sbif: selectedAccount.bank_sbif_code,
      accountbank_type: selectedAccount.account_type,
      accountbank_num: selectedAccount.account_number,
    };

    createWithdrawal(withdrawalData, {
      onSuccess: response => {
        const body = response?.data;
        const ok = response?.status >= 200 && response?.status < 300 && body?.status !== 0;
        if (ok) {
          setSuccessInfo({
            amount,
            bankName: selectedAccount.bank_name,
            accountNumber: selectedAccount.account_number,
          });
          setFeedback(null);
        } else {
          setFeedback({type: "error", text: body?.message || t("withdrawalPaykuError")});
        }
      },
      onError: err => {
        const apiMessage = err?.response?.data?.message;
        setFeedback({type: "error", text: apiMessage || err?.message || t("withdrawalPaykuError")});
      },
    });
  };

  const handleSuccessClose = () => {
    setSuccessInfo(null);
    if (onCancel) onCancel();
  };

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  if (!isValidCurrency) {
    return (
      <div className={styles.unsupportedContainer}>
        <h3 className={styles.unsupportedTitle}>{t("withdrawalPaykuUnsupportedCurrency")}</h3>
        <p className={styles.unsupportedMessage}>{t("withdrawalPaykuOnlyCLP")}</p>
        <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 1: RUT registration (if not yet registered)
  // ---------------------------------------------------------------------------

  if (!userLoading && !hasStoredRUT) {
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>{t("withdrawalPaykuRegisterRUTTitle")}</h2>
        <p className={styles.balanceInfo}>{t("withdrawalPaykuRegisterRUTDescription")}</p>

        <form onSubmit={handleRegisterRUT}>
          <div className={styles.formGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuRUT")} <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                value={rutInput}
                onChange={e => setRutInput(e.target.value)}
                placeholder={t("withdrawalPaykuRUTPlaceholder")}
              />
              <p className={styles.fieldHint}>{t("withdrawalPaykuRUTRegistrationWarning")}</p>
            </div>
          </div>

          {rutFeedback && (
            <div className={rutFeedback.type === "error" ? styles.errorFeedback : styles.successFeedback}>
              {rutFeedback.text}
            </div>
          )}

          <div className={styles.actionsBar}>
            <PrimaryButton
              type="submit"
              disabled={rutPending}
              fullWidth
            >
              {rutPending ? "Procesando..." : t("withdrawalPaykuRegisterRUTSubmit")}
            </PrimaryButton>
            <div className={styles.secondaryRow}>
              <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
              <SecondaryButton onClick={onCancel}>{t("depositInstructionsCancel")}</SecondaryButton>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 2: Create first bank account (if no accounts yet)
  // OR the user clicked "add new account" from the select step
  // ---------------------------------------------------------------------------

  if (step === "createAccount" || (!accountsLoading && hasStoredRUT && !hasAccounts)) {
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>{t("withdrawalPaykuCreateAccountTitle")}</h2>
        <p className={styles.balanceInfo}>
          {t("withdrawalPaykuRUT")}: <strong>{formatRUTForDisplay(storedRUT)}</strong>
          <IconLock
            size={14}
            style={{marginLeft: 6, verticalAlign: "middle"}}
          />
        </p>

        <form onSubmit={handleCreateAccount}>
          <div className={styles.formGrid}>
            {/* Beneficiary Name */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuBeneficiaryName")} <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                value={newAccount.beneficiary_name}
                onChange={e => setNewAccount(prev => ({...prev, beneficiary_name: e.target.value}))}
                placeholder={t("withdrawalPaykuBeneficiaryNamePlaceholder")}
              />
            </div>

            {/* Bank & Account Type */}
            <div className={styles.twoColumns}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  {t("withdrawalPaykuBank")} <span className={styles.required}>*</span>
                </label>
                <select
                  className={styles.select}
                  value={(() => {
                    const banks = banksData ?? [];
                    const idx = banks.findIndex(
                      b =>
                        (newAccount.bank_sbif_code && resolveBankCode(b) === newAccount.bank_sbif_code) ||
                        (b.name && b.name === newAccount.bank_name)
                    );
                    return idx >= 0 ? String(idx) : "";
                  })()}
                  onChange={handleBankSelect}
                  disabled={banksLoading}
                >
                  <option value="">{t("withdrawalPaykuSelectBank")}</option>
                  {(banksData ?? []).map((bank, idx) => (
                    <option
                      key={`${idx}-${bank.sbif ?? bank.code ?? bank.name}`}
                      value={idx}
                    >
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  {t("withdrawalPaykuAccountType")} <span className={styles.required}>*</span>
                </label>
                <select
                  className={styles.select}
                  value={newAccount.account_type}
                  onChange={e => setNewAccount(prev => ({...prev, account_type: Number(e.target.value)}))}
                >
                  {Object.entries(CHILE_ACCOUNT_TYPES).map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Account Number */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuAccountNumber")} <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                value={newAccount.account_number}
                onChange={e => setNewAccount(prev => ({...prev, account_number: e.target.value}))}
                placeholder={t("withdrawalPaykuAccountNumberPlaceholder")}
              />
            </div>

            {/* Email & Phone */}
            <div className={styles.twoColumns}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t("withdrawalPaykuEmail")}</label>
                <input
                  className={styles.input}
                  type="email"
                  value={newAccount.email}
                  onChange={e => setNewAccount(prev => ({...prev, email: e.target.value}))}
                  placeholder={t("withdrawalPaykuEmailPlaceholder")}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t("withdrawalPaykuPhone")}</label>
                <input
                  className={styles.input}
                  value={newAccount.phone}
                  onChange={e => setNewAccount(prev => ({...prev, phone: e.target.value}))}
                  placeholder={t("withdrawalPaykuPhonePlaceholder")}
                />
              </div>
            </div>

            <p className={styles.fieldHint}>{t("withdrawalPaykuRUTLockedHint")}</p>
          </div>

          {accountFeedback && (
            <div className={accountFeedback.type === "error" ? styles.errorFeedback : styles.successFeedback}>
              {accountFeedback.text}
            </div>
          )}

          <div className={styles.actionsBar}>
            <PrimaryButton
              type="submit"
              disabled={creatingAccount}
              fullWidth
            >
              {creatingAccount ? "Procesando..." : t("withdrawalPaykuCreateAccountSubmit")}
            </PrimaryButton>
            <div className={styles.secondaryRow}>
              {hasAccounts ? (
                <SecondaryButton onClick={() => setStep("selectAccount")}>
                  {t("depositInstructionsBack")}
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
              )}
              <SecondaryButton onClick={onCancel}>{t("depositInstructionsCancel")}</SecondaryButton>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 3: Select account & enter withdrawal amount
  // ---------------------------------------------------------------------------

  if (step === "selectAccount" && hasAccounts) {
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>{t("withdrawalPaykuTitle")}</h2>
        <p className={styles.balanceInfo}>
          {t("withdrawalPaykuAvailableBalance")}: <span className={styles.balanceAmount}>{formattedBalance}</span>
        </p>

        <form onSubmit={handleSubmitWithdrawal}>
          <div className={styles.formGrid}>
            {/* Account selection */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuSelectAccount")} <span className={styles.required}>*</span>
              </label>
              <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                {accounts.map(acct => (
                  <div
                    key={acct.id}
                    onClick={() => setSelectedAccount(acct)}
                    onKeyDown={e => e.key === "Enter" && setSelectedAccount(acct)}
                    role="button"
                    tabIndex={0}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `2px solid ${selectedAccount?.id === acct.id ? "var(--action-primary)" : "var(--border-primary)"}`,
                      background:
                        selectedAccount?.id === acct.id
                          ? "var(--bg-card-selected, var(--bg-secondary))"
                          : "var(--bg-secondary)",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <div>
                      <strong>{acct.bank_name}</strong>
                      <div style={{fontSize: 12, color: "var(--text-secondary)", marginTop: 2}}>
                        {CHILE_ACCOUNT_TYPES[acct.account_type]} · {acct.account_number}
                      </div>
                      <div style={{fontSize: 11, color: "var(--text-tertiary, var(--text-secondary))"}}>
                        {acct.beneficiary_name} · RUT {formatRUTForDisplay(acct.rut)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteAccount(acct.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--status-error)",
                        padding: 4,
                      }}
                      title={t("withdrawalPaykuDeleteAccount")}
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setStep("createAccount")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 8,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--action-primary)",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                <IconPlus size={14} />
                {t("withdrawalPaykuAddNewAccount")}
              </button>
            </div>

            {/* Amount */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuAmount")} <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWithSuffix}>
                <input
                  className={styles.input}
                  type="number"
                  value={withdrawalAmount}
                  onChange={e => setWithdrawalAmount(e.target.value)}
                  placeholder={t("withdrawalPaykuEnterAmount")}
                  min={MIN_WITHDRAWAL_CLP}
                  max={availableBalance}
                />
                <span className={styles.inputSuffix}>CLP</span>
              </div>
              <p className={styles.fieldHint}>{t("withdrawalPaykuMinAmount")}</p>
            </div>
          </div>

          {feedback && (
            <div className={feedback.type === "error" ? styles.errorFeedback : styles.successFeedback}>
              {feedback.text}
            </div>
          )}

          <div className={styles.actionsBar}>
            <PrimaryButton
              type="submit"
              disabled={withdrawPending || !selectedAccount}
              fullWidth
            >
              {withdrawPending ? "Procesando..." : t("withdrawalPaykuSubmit")}
            </PrimaryButton>
            <div className={styles.secondaryRow}>
              <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
              <SecondaryButton onClick={onCancel}>{t("depositInstructionsCancel")}</SecondaryButton>
            </div>
          </div>
        </form>

        {/* Success overlay */}
        {successInfo &&
          createPortal(
            <div
              className={styles.successOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby="payku-success-title"
              tabIndex={-1}
              onClick={handleSuccessClose}
              onKeyDown={e => {
                if (e.key === "Escape") handleSuccessClose();
              }}
            >
              <div
                className={styles.successCard}
                role="document"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <div className={styles.successIconWrap}>
                  <IconCircleCheck
                    size={56}
                    className={styles.successIcon}
                  />
                </div>
                <h3
                  id="payku-success-title"
                  className={styles.successTitle}
                >
                  {t("withdrawalPaykuSuccessTitle")}
                </h3>
                <p className={styles.successMessage}>{t("withdrawalPaykuSuccessMessage")}</p>
                <dl className={styles.successDetails}>
                  <div className={styles.successDetailRow}>
                    <dt>{t("withdrawalPaykuSuccessAmountLabel")}</dt>
                    <dd>{formatClpPesos(successInfo.amount)}</dd>
                  </div>
                  <div className={styles.successDetailRow}>
                    <dt>{t("withdrawalPaykuSuccessBankLabel")}</dt>
                    <dd>
                      {successInfo.bankName}
                      {successInfo.accountNumber ? ` · ${successInfo.accountNumber}` : ""}
                    </dd>
                  </div>
                </dl>
                <PrimaryButton
                  fullWidth
                  onClick={handleSuccessClose}
                >
                  {t("withdrawalPaykuSuccessClose")}
                </PrimaryButton>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }

  // Loading state
  return null;
};

PaykuWithdrawal.propTypes = {
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PaykuWithdrawal;
